import { Edit2, Hash, Lock, Settings2, Trash2 } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'
import useMediaQuery from '@/hooks/use-media-query'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { useAuth } from '@/contexts/auth-context'
import { Role } from '@/enums/role.enum'

import { Channel } from '@/types/group-channel.type'

interface ChannelItemProps {
  channel: Channel
  isActive: boolean
  onSelect: () => void
  onEdit: () => void
  onDelete: () => void
  unreadCount?: number
}

export default function ChannelItem({
  channel,
  isActive,
  onSelect,
  onEdit,
  onDelete,
  unreadCount = 0,
}: ChannelItemProps) {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false)
  const [isDescriptionPopoverOpen, setIsDescriptionPopoverOpen] = React.useState(false)
  const { user } = useAuth()
  const isAdmin = user?.roles.includes(Role.ADMIN)
  const isPublic = channel.status !== 'INACTIVE'
  const isMobile = useMediaQuery('(max-width: 640px)')

  const channelItemRef = React.useRef<HTMLLIElement>(null)
  const hasDescription = channel.description && channel.description.trim().length > 0

  return (
    <li
      className={cn(
        'flex items-center w-full px-3 sm:px-4 py-1 sm:py-1.5 text-sm text-sidebar-foreground hover:bg-blue-50 cursor-pointer hover:text-blue-700 transition-colors duration-200 group',
        isActive && 'bg-blue-100 text-blue-800 shadow-sm'
      )}
      onMouseEnter={() => setIsDescriptionPopoverOpen(true)}
      onMouseLeave={() => setIsDescriptionPopoverOpen(false)}
      ref={channelItemRef}
    >
      <div className="flex items-center flex-1 min-w-0" onClick={onSelect}>
        {isPublic ? (
          <Hash
            className={cn(
              'w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-muted-foreground group-hover:text-blue-600 transition-colors duration-200 flex-shrink-0',
              isActive && 'text-blue-800'
            )}
          />
        ) : (
          <Lock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-muted-foreground group-hover:text-blue-600 transition-colors duration-200 flex-shrink-0" />
        )}
        {hasDescription ? (
          <Popover open={isDescriptionPopoverOpen} onOpenChange={setIsDescriptionPopoverOpen}>
            <PopoverTrigger asChild>
              <span className="truncate min-w-0 text-xs sm:text-sm cursor-help">{channel.name}</span>
            </PopoverTrigger>
            <PopoverContent
              className="w-80 p-3 bg-white border border-gray-200 shadow-lg pointer-events-none z-[1000000]"
              side="right"
              align="start"
              sideOffset={channelItemRef.current ? channelItemRef.current.getBoundingClientRect().width - 140 : 144}
              onMouseEnter={() => setIsDescriptionPopoverOpen(true)}
              onMouseLeave={() => setIsDescriptionPopoverOpen(false)}
            >
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 text-sm">
                  Channel: <span className="text-blue-600">{channel.name}</span>
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">{channel.description}</p>
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <span className="truncate min-w-0 text-xs sm:text-sm">{channel.name}</span>
        )}
      </div>
      {unreadCount > 0 && (
        <div
          className={cn(
            'ml-2 mr-1 inline-flex items-center justify-center rounded-full text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 font-medium flex-shrink-0',
            isActive ? 'bg-blue-600 text-white' : 'bg-red-600 text-white'
          )}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </div>
      )}
      {isAdmin && (
        <Popover modal={isMobile} open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 sm:h-6 sm:w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-blue-100 hover:text-blue-800 flex-shrink-0"
              onClick={e => e.stopPropagation()}
            >
              <Settings2 className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-2">
            <button
              onClick={() => {
                onEdit()
                isMobile && setIsMobileSidebarOpen(false)
              }}
              className="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Edit2 className="h-4 w-4" />
              <span>Chỉnh sửa</span>
            </button>
            <button
              onClick={() => {
                onDelete()
                isMobile && setIsMobileSidebarOpen(false)
              }}
              className="flex items-center w-full gap-2 px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>Xóa</span>
            </button>
          </PopoverContent>
        </Popover>
      )}
    </li>
  )
}
